import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {TECHNICAL_SEO_POINTS, TEXT_IMAGES_ID} from '@axyseo/const/analysis';

/**
 * Represents the assessment that assesses the SEO title width and gives the feedback accordingly.
 */
export default class TextImagesAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object}  [config]        The configuration to use.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: TEXT_IMAGES_ID,
      ctaType: 'fix',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-audit/on-page-seo/checklist#alt-text',
      fixPosition: 'description',
      priority: 'high',
      title: 'Alt text',
      content: {
        good: 'All images have descriptive and relevant alt text.',
        improve: '',
        bad: 'Missing alt text. Add descriptive alt text for accessibility and SEO.'
      }
    };

    this.identifier = TEXT_IMAGES_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Runs the h1 research and based on this returns an assessment result with a score.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   * @param {Researcher}  researcher  The researcher used for calling the research.
   * @returns {AssessmentResult} The assessment result.
   */
  getResult({paper, researcher}) {
    const assessmentResult = new AssessmentResult({config: this._config});
    this.altTagsProperties = researcher.getResearch('altTagCount');
    this.imageCount = researcher.getResearch('imageCount');

    const calculatedResult = this.calculateResult(paper);

    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   *
   * @returns {{score: number, status: string}}
   */
  calculateResult() {
    const imagesNoAlt = this.altTagsProperties.noAlt;

    let status = 'good';
    if (imagesNoAlt === this.imageCount || imagesNoAlt > 0) {
      status = 'bad';
    }

    return {
      score:  this.getScore(TECHNICAL_SEO_POINTS, status) || 0,
      status
    };
  }

  /**
   *
   * @param shop
   * @param data
   * @returns {string}
   */
  fix({shop, data}) {
    if (!shop) {
      console.log('Shop is missing');
      return data.body || '';
    }

    if (!data.body) {
      console.log('body is missing');
      return '';
    }

    const altText = shop?.name || 'Shop' + ' | ' + data?.title || 'Title';

    const parser = new DOMParser();
    const doc = parser.parseFromString(data.body, 'text/html');

    const imgTags = doc.querySelectorAll('img');

    imgTags.forEach(img => {
      if (!img.hasAttribute('alt') || img.getAttribute('alt') === '') {
        img.setAttribute('alt', altText);
      }
    });

    return doc.body.innerHTML;
  }

  /**
   * Checks whether the paper has a text.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   * @param researcher
   * @returns {boolean} True when there is text.
   */
  isApplicable(paper, researcher) {
    return true;
  }
}
